import { X } from 'lucide-react';
import React, { useRef, useState } from 'react';
import { h3, p, textStyle } from '../../styles/typography';
import PrimaryButton from '../ui/PrimaryButton';
import Card from '../ui/card';
import './HyperLocalReferralTracker.css';
import { createAttribution, getReferralCodeByCode } from './referralTrackerApi';

const ApplyCouponOverlay = ({ referral, referrals = [], onClose, onApply, onCodeCreated, orderId, orderValue, customerId, customerName }) => {
  const [selectedReferral, setSelectedReferral] = useState(referral || null);
  const [couponCode, setCouponCode] = useState('');
  const [personName, setPersonName] = useState('');
  const [isApplying, setIsApplying] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [codeValidation, setCodeValidation] = useState({ isValid: null, message: '' });
  const [validatedCodeData, setValidatedCodeData] = useState(null);
  const validationTimeoutRef = useRef(null);

  const validateReferralCode = async (code) => {
    if (!code.trim()) {
      setCodeValidation({ isValid: null, message: '' });
      return false;
    }

    setIsValidating(true);
    setCodeValidation({ isValid: null, message: 'Validating code...' });

    try {
      const trimmedCode = code.trim().toUpperCase();
      const result = await getReferralCodeByCode(trimmedCode);
      
      if (result && result.referral_code) {
        setCodeValidation({ 
          isValid: true, 
          message: 'Valid referral code' 
        });
        setValidatedCodeData(result); // Store validated code data for later use
        setError('');
        return true;
      } else {
        setCodeValidation({ 
          isValid: false, 
          message: 'Invalid referral code' 
        });
        setValidatedCodeData(null);
        return false;
      }
    } catch (err) {
      console.error('Error validating referral code:', err);
      
      // Show user-friendly error messages
      let userFriendlyMessage = 'Invalid referral code';
      
      if (err?.isNotFound || err?.status === 404) {
        userFriendlyMessage = 'Invalid referral code';
      } else if (err?.message) {
        const errorMsg = err.message.toLowerCase();
        if (errorMsg.includes('404') || errorMsg.includes('not found')) {
          userFriendlyMessage = 'Invalid referral code';
        } else if (errorMsg.includes('401') || errorMsg.includes('unauthorized')) {
          userFriendlyMessage = 'Authentication error. Please sign in again.';
        } else if (errorMsg.includes('network') || errorMsg.includes('fetch')) {
          userFriendlyMessage = 'Network error. Please check your connection and try again.';
        }
      }
      
      setCodeValidation({ 
        isValid: false, 
        message: userFriendlyMessage
      });
      setValidatedCodeData(null);
      return false;
    } finally {
      setIsValidating(false);
    }
  };

  const handleCodeChange = (e) => {
    const value = e.target.value.toUpperCase();
    setCouponCode(value);
    setError('');
    setSuccess('');
    setCodeValidation({ isValid: null, message: '' });
    setValidatedCodeData(null);
    
    // Clear previous timeout
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current);
    }
    
    // Validate when user stops typing (debounce)
    if (value.trim().length >= 3) {
      validationTimeoutRef.current = setTimeout(() => {
        validateReferralCode(value);
      }, 500);
    }
  };

  const handleApply = async () => {
    const referralToUse = selectedReferral || referral;
    if (!referralToUse?.influencer_id) {
      setError('Please select an influencer');
      return;
    }

    if (!couponCode.trim()) {
      setError('Please enter a coupon code');
      return;
    }

    // Validate code before applying
    let isValid = codeValidation.isValid;
    
    // If not validated yet, validate now
    if (isValid === null) {
      isValid = await validateReferralCode(couponCode);
    }
    
    if (isValid === false) {
      setError('Please enter a valid referral code');
      return;
    }

    setIsApplying(true);
    setError('');
    setSuccess('');

    try {
      const codeToUse = couponCode.trim().toUpperCase();
      const appliedBy = personName.trim() || null;
      
      // Get influencer_id from validated code data or selected referral
      const influencerId = validatedCodeData?.influencer_id || referralToUse?.influencer_id || referralToUse?.id;
      
      if (!influencerId) {
        throw new Error('Unable to determine influencer ID. Please select an influencer.');
      }

      // Generate order_id if not provided (for testing/demo purposes)
      const finalOrderId = orderId || `ORDER-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      // Prepare attribution payload
      const attributionPayload = {
        order_id: finalOrderId,
        influencer_id: influencerId,
        referral_code_applied: codeToUse,
      };

      // Add optional fields if provided
      if (customerId) {
        attributionPayload.customer_id = customerId;
      }
      if (customerName || appliedBy) {
        attributionPayload.customer_name = customerName || appliedBy;
      }
      if (appliedBy) {
        attributionPayload.applied_by = appliedBy;
      }
      if (orderValue != null) {
        attributionPayload.order_value = Number(orderValue);
      }
      if (validatedCodeData?.site_id || referralToUse?.site_id) {
        attributionPayload.site_id = validatedCodeData?.site_id || referralToUse?.site_id;
      }

      // Apply referral code via API (POST /marketing/attributions)
      const attributionResult = await createAttribution(attributionPayload);
      
      console.log('Referral code applied successfully:', attributionResult);

      // Ensure we have a referral object with influencer_id for the callback
      const referralForCallback = referralToUse?.influencer_id 
        ? referralToUse 
        : { ...referralToUse, influencer_id: influencerId };

      // Call the onApply callback if provided
      if (onApply) {
        await onApply(codeToUse, referralForCallback, appliedBy, attributionResult);
      }

      // Notify parent component that code was applied
      if (onCodeCreated) {
        onCodeCreated(codeToUse, referralForCallback, appliedBy, attributionResult);
      }

      setSuccess(`Coupon code "${codeToUse}" applied successfully!${appliedBy ? ` Applied by: ${appliedBy}` : ''}`);
      setTimeout(() => {
        onClose?.();
      }, 1500);
    } catch (err) {
      console.error('Error applying coupon code:', err);
      setError(err?.message || 'Failed to apply coupon code. Please try again.');
    } finally {
      setIsApplying(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleApply();
    }
  };

  return (
    <div className="referral-tracker-details">
      <div className="referral-tracker-details-header">
        <h2 className={textStyle(h3, 'referral-tracker-details-title')}>
          Apply Coupon Code
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="referral-tracker-details-close"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <Card variant="light" className="referral-tracker-details-card p-5">
        {(referrals.length > 0 || referral) && (
          <div className="mb-4">
            <label className={textStyle(p, 'block text-gray-700 font-semibold mb-2')}>
              Select Influencer
            </label>
            <select
              value={selectedReferral?.influencer_id || referral?.influencer_id || ''}
              onChange={(e) => {
                const selected = referrals.find(r => (r.influencer_id || r.id) === e.target.value) || referral;
                setSelectedReferral(selected || null);
                setError('');
                setSuccess('');
                setCodeValidation({ isValid: null, message: '' });
                setValidatedCodeData(null);
              }}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40 text-sm"
            >
              <option value="">-- Select an influencer --</option>
              {referral && (
                <option value={referral.influencer_id || referral.id}>
                  {referral.name || 'Unnamed'}
                </option>
              )}
              {referrals.map((r) => (
                <option key={r.influencer_id || r.id} value={r.influencer_id || r.id}>
                  {r.name || 'Unnamed'}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="mb-4">
          <label className={textStyle(p, 'block text-gray-700 font-semibold mb-2')}>
            Referral Code
          </label>
          <input
            type="text"
            value={couponCode}
            onChange={handleCodeChange}
            onKeyPress={handleKeyPress}
            placeholder="Enter referral code"
            className={`w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 text-sm font-mono ${
              codeValidation.isValid === true
                ? 'border-green-500 focus:border-green-500 focus:ring-green-500/40'
                : codeValidation.isValid === false
                ? 'border-red-500 focus:border-red-500 focus:ring-red-500/40'
                : 'border-gray-300 focus:border-green-500 focus:ring-green-500/40'
            }`}
            disabled={isValidating}
          />
          {codeValidation.message && (
            <p className={`text-xs mt-2 ${
              codeValidation.isValid === true
                ? 'text-green-600'
                : codeValidation.isValid === false
                ? 'text-red-600'
                : 'text-gray-500'
            }`}>
              {codeValidation.message}
            </p>
          )}
          {!codeValidation.message && (
            <p className={textStyle(p, 'text-gray-500 text-xs mt-2')}>
              Enter the referral code manually
            </p>
          )}
        </div>

        <div className="mb-4">
          <label className={textStyle(p, 'block text-gray-700 font-semibold mb-2')}>
            Person Name (Optional)
          </label>
          <input
            type="text"
            value={personName}
            onChange={(e) => {
              setPersonName(e.target.value);
              setError('');
              setSuccess('');
            }}
            placeholder="Enter name of person applying the referral"
            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40 text-sm"
          />
          <p className={textStyle(p, 'text-gray-500 text-xs mt-2')}>
            Optional: Enter the name of the person applying this referral code
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200">
            <p className={textStyle(p, 'text-red-600 text-sm')}>{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 rounded-lg bg-green-50 border border-green-200">
            <p className={textStyle(p, 'text-green-600 text-sm')}>{success}</p>
          </div>
        )}

        <div className="flex justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={onClose}
            disabled={isApplying}
            className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
          >
            Cancel
          </button>
          <PrimaryButton
            type="button"
            onClick={handleApply}
            disabled={isApplying || isValidating || (!selectedReferral && !referral) || codeValidation.isValid === false}
            className="px-6 py-2 text-sm"
          >
            {isApplying ? 'Applying...' : isValidating ? 'Validating...' : 'Apply Coupon'}
          </PrimaryButton>
        </div>
      </Card>
    </div>
  );
};

export default ApplyCouponOverlay;

